import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { STATUS } from "../../../theme/tokens";

type Props = {
	/** The capped hours actually filed for this row (day scaled to the CMS cap). */
	cappedHours: number;
	/** The real, uncapped worked hours — shown in the tooltip so reviewers see what was adjusted. */
	originalHours: number;
	/** Distinguishes the badge across the Staffing-Hours surfaces. */
	dataTestId: string;
};

/**
 * CMS daily-cap marker: shows the FILED (capped) hours for an employee-day that exceeded the CMS
 * limit of 22.5 hours per employee per day, with a tooltip explaining the override and citing the
 * real worked hours. The grid/raw-export display this in place of `hours`; the underlying `hours`
 * value is unchanged. Single source for the glyph, tone token and copy so the surfaces never drift.
 */
export const CmsCapBadge: FC<Props> = ({ cappedHours, originalHours, dataTestId }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			<Box component="span">{cappedHours.toFixed(2)}</Box>
			<InfoIconWithTooltip
				title={t("pbj_model.staffing.cms_cap.tooltip", { original: originalHours.toFixed(2), capped: cappedHours.toFixed(2) })}
				icon={InfoOutlinedIcon}
				iconSx={{ fontSize: "16px", color: STATUS.WARNING.main }}
				dataTestId={dataTestId}
			/>
		</Box>
	);
};
